import { $, $$, getTopPosition } from '../../../shared/js/utils/dom';
import { currentBreakpoint } from '../../../shared/js/globals/breakpoints';
import { headerHeightFixed } from '../../../shared/js/globals/header';
import { Shower } from '../../../shared/js/modules/shower';
import Bus from '../../../shared/js/modules/event-bus';
import { generateRandomString } from '../../../shared/js/utils/unique-string';

const navAnchors = $('.nav-anchors');
let navAnchorsSticky;
let navAnchorsControls;
let navAnchorsContent;
let navAnchorsLinks;
let anchorTargetSelectors;
let anchorTargets;
let anchorTargetsPos = [];
let showerToggle;
let current;
let currentInitialText;
let currentIndex;
let prevIndex;
let nextIndex;
let shuffleUp;
let shuffleUpWrapper;
let shuffleDown;
let shuffleDownWrapper;
let showerContent;
let showerInstance;

const collectDom = () => {
  navAnchorsSticky = $('.nav-anchors-sticky', navAnchors);
  if (!navAnchorsSticky) return;
  navAnchorsControls = $('.nav-anchors-sticky__controls', navAnchorsSticky);
  navAnchorsContent = $('.nav-anchors-sticky__content', navAnchorsSticky);
  navAnchorsLinks = $$('a', navAnchorsContent);
  anchorTargetSelectors = navAnchorsLinks.map(link => link.hash).toString();
  anchorTargets = $$(anchorTargetSelectors);
  showerToggle = $('.nav-anchors-sticky__button--toggle', navAnchorsControls);
  current = $('.nav-anchors-sticky__current', navAnchorsControls);
  currentInitialText = current.textContent.trim();
  shuffleUp = $('.nav-anchors-sticky__button--up', navAnchorsControls);
  shuffleUpWrapper = shuffleUp.closest('.nav-anchors-sticky__button-wrapper');
  shuffleDown = $('.nav-anchors-sticky__button--down', navAnchorsControls);
  shuffleDownWrapper = shuffleDown.closest(
    '.nav-anchors-sticky__button-wrapper'
  );
  showerContent = $('.nav-anchors-sticky__content', navAnchorsSticky);
};

// Sticky Anchors
const stickyHandler = () => {
  const isSticky = window.pageYOffset > getTopPosition(navAnchors);
  if (isSticky) {
    const breakpoint = currentBreakpoint;
    const headerHeight = headerHeightFixed[breakpoint];
    navAnchorsSticky.style.position = 'fixed';
    navAnchorsSticky.style.top = `${headerHeight}px`;
    navAnchorsSticky.classList.remove('is-hidden');
    return;
  }
  navAnchorsSticky.classList.add('is-hidden');
  navAnchorsSticky.style.position = 'static';
};

// Anchors Shower
const setupShower = () => {
  if (!showerContent || !showerToggle) return;

  // Reset existing shower (e.g. from before onResize)
  navAnchorsSticky.classList.remove('is-hidden');
  showerInstance?.destroy();
  showerToggle.removeAttribute('aria-controls');
  showerToggle.removeEventListener('keydown', onKeydown);
  showerContent.removeEventListener('keydown', onKeydown);

  showerInstance = new Shower({
    target: showerContent,
    handler: showerToggle,
    duration: 300,
    initOpened: false,
    openClassName: 'is-open',
    toggleStart: (_, state) => {
      //   if (state === 'show') {}
    },
    toggleEnd: (_, state) => {
      //   if (state !== 'show') {}
    },
  });

  showerInstance.init();
  navAnchorsSticky.classList.add('is-hidden');
  setupAriaAttributes(showerToggle, showerContent);
  showerToggle.addEventListener('keydown', onKeydown);
  showerContent.addEventListener('keydown', onKeydown);
};

// Nav Controls
const updateNavAnchorTargetsPos = () => {
  anchorTargetsPos = anchorTargets.map(anchor => getTopPosition(anchor));
  anchorTargetsPos.push(document.body.clientHeight);
};

const updateIndexes = () => {
  const index = anchorTargetsPos.findIndex(
    pos =>
      pos -
        headerHeightFixed[currentBreakpoint] -
        navAnchorsSticky.clientHeight -
        20 > // Register 20px below sticky anchor nav.
      window.pageYOffset
  );
  if (index < 0) return;
  currentIndex = index;

  // Ensure that the last anchor always "passes" as current,
  // if the site is no further y-scrollable and thus could actually
  // never satisfy the above position check.
  if (
    Math.ceil(window.pageYOffset + window.innerHeight) >=
    document.body.clientHeight
  )
    currentIndex = anchorTargetsPos?.length - 1;

  prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : false;
  nextIndex = currentIndex + 1;
};

const updateNavAnchorsCurrent = () => {
  if (prevIndex === false) {
    current.innerText = currentInitialText;
    return;
  }
  current.innerText = navAnchorsLinks[prevIndex].textContent.trim();
};

const updateNavPrevNext = () => {
  updateNavNext();
  updateNavPrev();
};

const updateNavPrev = () => {
  if (prevIndex === false || prevIndex <= 0) {
    shuffleUpWrapper.setAttribute('data-disabled', true);
    return;
  }
  shuffleUp.href = navAnchorsLinks[prevIndex - 1].href;
  shuffleUpWrapper.removeAttribute('data-disabled');
};

const updateNavNext = () => {
  if (currentIndex >= navAnchorsLinks.length) {
    shuffleDownWrapper.setAttribute('data-disabled', true);
    return;
  }
  shuffleDown.href = navAnchorsLinks[currentIndex].href;
  shuffleDownWrapper.removeAttribute('data-disabled');
};

const jumpToNextAnchor = e => {
  e.preventDefault();
  if (nextIndex === false) return;
  anchorTargets[currentIndex].scrollIntoView({
    behavior: 'smooth',
  });
  anchorTargets[currentIndex].focus({
    preventScroll: true,
  });
};

const jumpToPrevAnchor = e => {
  e.preventDefault();
  if (prevIndex === false) return;
  anchorTargets[prevIndex - 1].scrollIntoView({
    behavior: 'smooth',
  });
  anchorTargets[prevIndex - 1].focus({
    preventScroll: true,
  });
};

const controlsEventBinding = () => {
  shuffleDown.addEventListener('click', jumpToNextAnchor);
  shuffleUp.addEventListener('click', jumpToPrevAnchor);
};

// Aria-attributes

const setupAriaAttributes = (toggle, content) => {
  const uniqueId = `nav-anchors-sticky-content-${generateRandomString(
    toggle.offsetTop
  )}`;
  toggle.setAttribute('aria-controls', uniqueId);
  content.setAttribute('id', uniqueId);
};

// Event handler

const onResize = () => {
  updateNavAnchorTargetsPos();
  setupShower();
};

const onScroll = () => {
  stickyHandler();
  updateNavAnchorTargetsPos();
  updateIndexes(); // Must come before NavCurrent and NavPrev/Next updates.
  updateNavAnchorsCurrent();
  updateNavPrevNext();
};

const onKeydown = e => {
  if (e.key === 'Escape') {
    showerInstance.close();
    showerToggle.focus();
  }
};

// Initialize header
const init = () => {
  if (!navAnchors) return;
  collectDom();
  if (!navAnchorsSticky) return;
  navAnchorsSticky.setAttribute('aria-hidden', true);
  controlsEventBinding();
  setupShower();
  updateNavAnchorTargetsPos();
  Bus.subscribe('resize', 'navAnchorsStickyResize', onResize);
  Bus.subscribe('scroll', 'navAnchorsStickyScroll', onScroll);
};

init();
